import {
  AuthenticationActions,
  EAuthenticationActions
} from '../actions/authentication.actions';
import { EMultipleAuthViewMode } from '../../core/security-authentication.enum';
import { DomainAuthenticator, SecurityAuthenticationState } from '../../core/authentication-contracts';

export const initialSecurityAuthenticationState: SecurityAuthenticationState = {
  activeFlow: '',
  authenticationData: null,
  currentAuthenticator: null,
  currentAuthenticatorOptionsMetadata: null,
  authenticatorList: null,
  multipleOptions: null,
  currentOption: null,
  isLoading: false,
  isLoadingWindowStatus: false,
  isAvailableForChanges: null,
  metadata: null,
  multipleViewMode: EMultipleAuthViewMode.Unselect,
  selectedAuthenticator: null,
  selectedFlow: null,
  multipleOptionsDefaultValues: null
};

export function AuthenticationReducer(
  state = initialSecurityAuthenticationState,
  action: AuthenticationActions
): SecurityAuthenticationState {
  switch (action.type) {
    case EAuthenticationActions.GetMetadata: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoading: true
      };
      return newState;
    }

    case EAuthenticationActions.GetMetadataSuccess: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoading: false,
        metadata: { ...action.payload }
      };
      return newState;
    }

    case EAuthenticationActions.GetMultipleOptions: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoading: true
      };
      return newState;
    }

    case EAuthenticationActions.GetMultipleOptionsSuccess: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoading: false,
        multipleOptions: action.payload
      };
      return newState;
    }

    case EAuthenticationActions.GetMaintenanceWindowStatus: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoadingWindowStatus: true
      };
      return newState;
    }

    case EAuthenticationActions.GetAuthenticationIsValidSuccess: {
      const newState: SecurityAuthenticationState = {
        ...state,
        authenticationIsValid: action.payload
      };
      return newState;
    }

    case EAuthenticationActions.GetMaintenanceWindowStatusSuccess: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoadingWindowStatus: false,
        isAvailableForChanges: action.payload
      };
      return newState;
    }

    case EAuthenticationActions.GetAuthenticationData: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoading: true
      };
      return newState;
    }

    case EAuthenticationActions.GetAuthenticationDataSuccess: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoading: true,
        authenticationData: _.cloneDeep(action.payload),
        authenticatorList: _.cloneDeep(action.payload[0].content.multipleMethod)
      };
      return newState;
    }

    case EAuthenticationActions.SetMultipleViewMode: {
      const newState = {
        ...state,
        multipleViewMode: action.payload
      };
      return newState;
    }

    case EAuthenticationActions.SetCurrentAuthenticator: {
      let currentOptionsMetadata = null;
      let options = null;
      if (action.payload && action.payload.type) {
        currentOptionsMetadata = state.multipleOptions
          .find(element => element.type === action.payload.type && element.subtype === action.payload.subtype);

        if (action.payload.type === 'OAuth2') {
          options = currentOptionsMetadata.oauthOptions[action.payload.oauthOptions.activeFlow];
        } else {
          options = currentOptionsMetadata ? currentOptionsMetadata.options : {};
        }
      }

      const newState = {
        ...state,
        currentAuthenticator: _.cloneDeep(action.payload),
        currentAuthenticatorOptionsMetadata: _.cloneDeep(options),
      };
      return newState;
    }

    case EAuthenticationActions.UpdateAuthenticatorOption: {
      const authenticator = _.find(state.authenticationData[0].content.multipleMethod.authenticators,
        (element) => element.displayName === state.currentAuthenticator.displayName && element.type === state.currentAuthenticator.type);

      if (authenticator) {
        if (authenticator.type === 'OAuth2') {
          const currentFlow = _.find(authenticator.oauthOptions.flows, flow => flow.name === authenticator.oauthOptions.activeFlow);
          const currentOption = _.find(currentFlow.properties, property => property.name === action.payload.name);
          currentOption.value = action.payload.value;
        } else {
          authenticator.options[action.payload.key] = action.payload.value;
        }
      }

      const newState: SecurityAuthenticationState = {
        ...state,
        currentAuthenticator: _.cloneDeep(authenticator),
        authenticationData: _.cloneDeep(state.authenticationData),
        authenticatorList: _.cloneDeep(state.authenticationData[0].content.multipleMethod)
      };
      return newState;
    }

    case EAuthenticationActions.AddNewMultipleAuthenticator: {
      const authenticatorsList = [...state.authenticationData[0].content.multipleMethod.authenticators];
      if (authenticatorsList) {
        authenticatorsList.push(action.payload);
      }

      const newState: SecurityAuthenticationState = {
        ...state,
        authenticationData: _.cloneDeep(state.authenticationData),
        authenticatorList: _.cloneDeep(state.authenticationData[0].content.multipleMethod)
      };
      newState.authenticationData[0].content.multipleMethod.authenticators = authenticatorsList;
      return newState;
    }

    case EAuthenticationActions.UpdateMultipleAuthenticator: {
      const index = _.findIndex(
        state.authenticationData[0].content.multipleMethod.authenticators,
        element =>
          (element as any).displayName ===
          state.currentAuthenticator.displayName
      );

      let authenticatorsCopy = [...state.authenticationData[0].content.multipleMethod.authenticators];

      if (index !== -1) {
        authenticatorsCopy[index] = action.payload;
      }

      const newState: SecurityAuthenticationState = {
        ...state,
        currentAuthenticator: _.cloneDeep(action.payload),
        authenticationData: _.cloneDeep(state.authenticationData),
        authenticatorList: _.cloneDeep(state.authenticationData[0].content.multipleMethod)
      };

      newState.authenticationData[0].content.multipleMethod.authenticators = authenticatorsCopy;

      return newState;
    }

    case EAuthenticationActions.SaveAuthenticationData: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoading: true
      };
      return newState;
    }

    case EAuthenticationActions.UpdateAuthenticationDataSuccess: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoading: false
      };
      return newState;
    }

    case EAuthenticationActions.DeleteAuthenticators: {
      _.forEach(action.payload, authenticatorToDelete =>
        _.remove(state.authenticationData[0].content.multipleMethod.authenticators,
          authenticator => (authenticator as any).displayName === authenticatorToDelete.displayName)
      );

      const newState: SecurityAuthenticationState = {
        ...state,
        authenticationData: _.cloneDeep(state.authenticationData),
        authenticatorList: _.cloneDeep(state.authenticationData[0].content.multipleMethod)
      };
      return newState;
    }

    case EAuthenticationActions.CleanMultipleAuthState: {
      const newState: SecurityAuthenticationState = _.cloneDeep(initialSecurityAuthenticationState);
      return newState;
    }

    case EAuthenticationActions.UpdateDomainList: {
      const domainList: DomainAuthenticator[] = [...action.payload];
      const currentAuthenticator = _.cloneDeep(state.currentAuthenticator);
      currentAuthenticator.domains = domainList;
      const newState: SecurityAuthenticationState = {
        ...state,
        currentAuthenticator
      }
      return newState;
    }

    case EAuthenticationActions.GetMultipleOptionsDefaultValues: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoading: true
      };
      return newState;
    }

    case EAuthenticationActions.GetMultipleOptionsDefaultValuesSuccess: {
      const newState: SecurityAuthenticationState = {
        ...state,
        isLoading: false,
        multipleOptionsDefaultValues: action.payload
      };
      return newState;
    }

    default: {
      return state;
    }
  }
}
